import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable, map } from 'rxjs';
import { API_URL_V4, environment } from 'src/environments/environment.local';
import {
  Playlist,
  PlaylistHttpResult,
  PlaylistsHttpResult,
} from '../models/playlist.model';
import { IPlaylistApi } from './interfaces/playlist-abstract.api';
import { ApiHttpResponse, VideoSection } from '@kinomap-dev/ng-ui-lib';

@Injectable({
  providedIn: 'root',
})
export class PlaylistApi implements IPlaylistApi {
  private readonly http: HttpClient = inject(HttpClient);

  getPlaylists(page: number, limit: number): Observable<PlaylistsHttpResult> {
    const params: HttpParams = new HttpParams()
      .set('page', page)
      .set('limit', limit);
    const url: string = environment.api.dashboard.myPlaylists;
    return this.http.get<PlaylistsHttpResult>(url, { params });
  }

  getPlaylist(playlistId: number): Observable<Playlist> {
    const url: string = environment.api.playlists.getPlaylist.replace(
      '{playlistId}',
      '' + playlistId
    );

    return this.http
      .get<PlaylistHttpResult>(url)
      .pipe(map((res: PlaylistHttpResult) => res.data));
  }

  editPlaylist(id: number, playlist: any): Observable<Playlist> {
    const url: string = environment.api.playlists.getPlaylist.replace(
      '{playlistId}',
      '' + id
    );

    const requestBody: FormData = new FormData();

    requestBody.append('title', playlist.title);
    requestBody.append('description', playlist.description);
    requestBody.append('status', playlist.status);

    if (playlist.banner)
      requestBody.append('banner', playlist.banner, playlist.banner.name);
    if (playlist.logo)
      requestBody.append('logo', playlist.logo, playlist.logo.name);

    return this.http
      .post<ApiHttpResponse<Playlist>>(url, requestBody)
      .pipe(map((res: ApiHttpResponse<Playlist>) => res.data));
  }

  deletePlaylist(playlistId: number): Observable<any> {
    const url: string = environment.api.dashboard.deletePlaylist.replace(
      '{playlist}',
      '' + playlistId
    );
    return this.http.delete<any>(url);
  }

  addVideoToPlaylist(
    playlistId: number,
    videoId: number,
    videoType: string
  ): Observable<any> {
    const url: string = environment.api.watch.addVideoToPlaylist.replace(
      '{playlist}',
      '' + playlistId
    );
    const requestBody: { type: string; videoId: number } = {
      type: videoType,
      videoId: videoId,
    };
    return this.http.post(url, requestBody);
  }

  addPlaylistToFavorite(playlistId: number) {
    const url: string =
      environment.api.dashboard.myFavoritesAddPlaylist.replace(
        '{playlist}',
        '' + playlistId
      );

    return this.http.post(url, playlistId);
  }

  removePlaylistFromFavorite(playlistId: number) {
    const url: string =
      environment.api.dashboard.myFavoritesAddPlaylist.replace(
        '{playlist}',
        '' + playlistId
      );

    return this.http.delete(url);
  }

  getVideoFromPlaylist(
    playlistId: number,
    page: number = 0,
    params: HttpParams = new HttpParams()
  ): Observable<ApiHttpResponse<VideoSection[]>> {
    const url: string = environment.api.watch.getVideoFromPlaylist.replace(
      '{playlist}',
      '' + playlistId
    );
    if (!params.get('page')) params = params.append('page', page);
    if (!params.get('limit')) params = params.append('limit', 15);
    return this.http.get<ApiHttpResponse<VideoSection[]>>(url, { params });
  }

  removeVideoFromPlaylist(
    playlistId: number,
    videoId: number
  ): Observable<VideoSection[]> {
    const url: string = `${API_URL_V4}/playlist/${playlistId}/remove/${videoId}`;
    const params: HttpParams = new HttpParams().set('trainingMode', 'scenic');

    return this.http.delete<VideoSection[]>(url, { params });
  }

  createNewPlaylist(
    title: string,
    desc: string,
    status: string,
    banner: File | null,
    logo: File | null,
    videoId: number,
    trainingMode: string,
    type: string
  ): Observable<any> {
    const requestBody: FormData = new FormData();

    requestBody.append('title', title);
    requestBody.append('description', desc);
    requestBody.append('status', status);
    requestBody.append('trainingMode', trainingMode);
    requestBody.append('videoId', videoId.toString());
    requestBody.append('type', type);

    requestBody.append('type', type);

    if (banner) requestBody.append('banner', banner, banner.name);
    if (logo) requestBody.append('logo', logo, logo.name);

    const url: string = environment.api.watch.createNewPlaylist;
    return this.http.post(url, requestBody);
  }
}
